import { BsLink45Deg } from 'react-icons/bs';
import { FiEdit2, FiTrash } from 'react-icons/fi';
import styled from 'styled-components';
import theme from '@/common/theme';

const TrashIcon = styled(FiTrash)`
  margin-left: 12px;
  cursor: pointer;
`;

const EditIcon = styled(FiEdit2)`
  cursor: pointer;
`;

const LinkIcon = styled(BsLink45Deg)`
  margin-right: 12px;
`;

const InputHeader = styled.div`
  margin-bottom: 10px;
  color: ${(props) => props.theme.inputHeaderColor};
  font-size: 14px;
  font-weight: 400;
`;

const TooltipContent = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  justify-content: space-between;
  font-size: 12px;
`;

const RedactorItemsDiv = styled.div`
  border: ${(props) => (props.newVersion ? 'none' : '1px solid #dde0e8')};
  padding: 4px;
`;

const Div = styled.div`
  font-size: 12px;
  margin-left: 12px;
`;

const EditorDiv = styled.div`
  border: ${(props) =>
    props.border ? `1px solid ${theme.COLOR_GREY5}` : 'none'};
  @media (hover: hover) {
    &:hover {
      border: ${(props) =>
        props.border ? `1px solid ${props.theme.inputHoverColor}` : 'none'};
    }
  }
  border-radius: 4px;
  padding: ${(props) => (props.newVersion ? '15px 15px 0 15px' : '15px')};
  margin-bottom: ${(props) => (props.fullScreen ? '0' : '15px')};
  height: ${(props) => (props.fullScreen ? '100%' : 'auto')};
  width: ${(props) => (props.fullScreen ? '100%' : 'auto')};
  color: ${(props) => props.theme.inputPrimaryFontColor};

  &.invalid {
    border: 1px solid $red;
  }
  &.valid {
    border: 1px solid ${(props) => props.theme.COLOR_PRIMARY};
  }

  .redactor-link {
    color: ${(props) => props.theme.COLOR_PRIMARY};
    text-decoration: underline;
    text-decoration-thickness: 3px;
  }

  .public-DraftEditorPlaceholder-inner {
    color: ${(props) => props.theme.COLOR_GREY4};
  }

  .DraftEditor-root {
    font-size: 14px;
    font-weight: 400;
    margin-bottom: 15px;
    line-height: 1.429;
    position: relative;
    height: ${(props) => (props.fullScreen ? 'calc(100% - 35px)' : 'auto')};
    color: ${(props) => props.theme.TEXT_COLOR};

    ::placeholder {
      color: ${(props) => props.theme.COLOR_GREY4};
    }

    .public-DraftEditorPlaceholder-root {
      color: ${(props) => props.theme.inputPrimaryFontColor};
      position: absolute;
    }
    .public-DraftStyleDefault-block {
      span {
        color: ${(props) => props.theme.inputPrimaryFontColor};
      }

      a {
        span {
          color: ${(props) => props.theme.linkColor};
        }
      }
    }
    .public-DraftEditor-content {
      color: ${(props) => props.theme.inputPrimaryFontColor};
    }

    .DraftEditor-editorContainer {
      min-height: ${(props) => (props.fullScreen ? '100%' : '200px')};
      max-height: ${(props) => (props.fullScreen ? '100%' : '49vh')};
      overflow-y: auto;
      ::-webkit-scrollbar {
        width: 10px;
        height: 10px;
        background: white;
      }

      /* Track */
      ::-webkit-scrollbar-track {
        border-radius: 5px;
        background: rgba(0, 0, 0, 0.2);
      }
      /* Handle */
      ::-webkit-scrollbar-thumb {
        background: rgba(0, 0, 0, 0.3);
        border-radius: 5px;
      }

      scrollbar-face-color: rgba(0, 0, 0, 0.3); /* Firefox 63 compatibility */
      scrollbar-track-color: rgba(0, 0, 0, 0.2); /* Firefox 63 compatibility */
      scrollbar-color: rgba(0, 0, 0, 0.3) rgba(0, 0, 0, 0.2);
      scrollbar-width: thin;
    }
  }

  .redactorItems {
    .material-icons {
      color: ${(props) => props.theme.inputUnderlineColor};
      margin-right: 15px;
      cursor: pointer;
      position: relative;
      padding: 2.5px;
      transition: border 0.2s ease-in-out;
      border: 2px solid transparent;

      &.active {
        color: ${(props) => props.theme.inputClickedColor};
      }
      @media (hover: hover) {
        &:hover {
          color: ${(props) => props.theme.inputHoverColor};
        }
      }

      &::before,
      &::after {
        box-sizing: inherit;
        content: '';
        position: absolute;
        width: 100%;
        height: 100%;
      }
      &::before,
      &::after {
        // Set border to invisible, so we don't see a 4px border on a 0x0 element before the transition starts
        border: 2px solid transparent;
        width: 0;
        height: 0;
      }

      // This covers the top & right borders (expands right, then down)
      &::before {
        top: 0px;
        left: 0px;
      }

      // And this the bottom & left borders (expands left, then up)
      &::after {
        bottom: 0px;
        right: 0px;
      }

      @media (hover: hover) {
        &:hover::before,
        &:hover::after {
          width: 100%;
          height: 100%;
        }
      }

      @media (hover: hover) {
        &:hover::before {
          border-top-color: ${(props) =>
            props.theme.inputHoverColor}; // Make borders visible
          border-right-color: ${(props) => props.theme.inputHoverColor};
          transition:
            width 0.125s ease-out,
            // Width expands first
            height 0.125s ease-out 0.125s; // And then height
        }
      }

      @media (hover: hover) {
        &:hover::after {
          border-bottom-color: ${(props) =>
            props.theme.inputHoverColor}; // Make borders visible
          border-left-color: ${(props) => props.theme.inputHoverColor};
          transition:
            border-color 0s ease-out 0.25s,
            // Wait for ::before to finish before showing border
            width 0.125s ease-out 0.25s,
            // And then exanding width
            height 0.125s ease-out 0.375s; // And finally height
        }
      }
    }
  }
`;

const StyledRedactor = {
  InputHeader,
  EditorDiv,
  TooltipContent,
  TrashIcon,
  RedactorItemsDiv,
  Div,
  EditIcon,
  LinkIcon,
};

export default StyledRedactor;
